
import { Injectable } from '@angular/core';

import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { gateCall } from '../../../Core/rxjs-utils';

import { BriefingConfigApiService } from '../../../api/monitoring/briefing-config.api.service';
import { BriefingConfigApi } from '../../../api/monitoring/model/briefing-config.api.model';

import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';


@Injectable()
export class BriefingConfigService {

    private configModel: BriefingConfigApi;
    private configModel$ = new BehaviorSubject<BriefingConfigApi>(null);

    private refreshConfig$ = new Subject<void>();

    private subscriptions: Subscription[] = [];

    constructor(
        private briefingAccountApiService: BriefingConfigApiService,
        private currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.refreshConfig$.pipe(
                switchMap(() => gateCall(this.hasBriefingConfigAccess())),
                switchMap(() => this.briefingAccountApiService.getConfig()),
                tap(configModel => {
                    this.setConfig(configModel);
                })
            ).subscribe()
        );

        this.refreshBriefingConfigModel();
    }

    setConfig(config: BriefingConfigApi) {
        this.configModel = config;
        this.configModel$.next(this.configModel);
    }

    getConfig(): Observable<BriefingConfigApi> {
        return this.configModel$;
    }

    /**
     * Refreshes the briefing config model
     */
    refreshBriefingConfigModel(): void {
        this.refreshConfig$.next();
    }

    getAlertLimit(): Observable<number> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.alertLimit)
        );
    }

    getAutoGenInsightAccess(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => x.insightAutoGenerationBriefings)
        );
    }

    isLimitGreaterThanActiveAlerts(): Observable<boolean> {
        return this.configModel$.pipe(
            filter(x => !!x),
            map(x => (x.alertLimit === -1) || (x.alertLimit > x.activeAlertTotal))
        );
    }

    /**
     * Observable completes after call, via {@link CurrentUserStateService.hasAnyPermissions}
     */
    hasBriefingConfigAccess(): Observable<boolean> {
        const configPermissions = [
            userPermissionsEnum.advancedMonitoring,
            userPermissionsEnum.socialIntelligence
        ];
        return this.currentUserStateService.hasAnyPermissions(configPermissions);
    }

    hasSocialConfigAccess(): Observable<boolean> {
        return this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.socialIntelligence]);
    }

    hasSubscriptions(): Observable<boolean> {
        return this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]);
    }
}
